import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  AdminRowActionsMenu,
  buildActionsColumn,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import {
  createGalleryCategory,
  fetchGalleryCategories,
  updateGalleryCategory,
} from "../../../lib/api";

function rowIsActive(row) {
  return row.active !== false;
}

const CATEGORY_COLUMNS = [
  { key: "sortOrder", label: "#", width: "64px", render: (r) => String(r.sortOrder ?? 0) },
  { key: "name", label: "Name", width: "20%" },
  {
    key: "slug",
    label: "Slug",
    width: "14%",
    render: (r) => {
      const s = String(r.slug ?? "").trim();
      return s || "—";
    },
  },
  {
    key: "description",
    label: "Description",
    width: "22%",
    tdClassName: "adm-data-cell-muted",
    render: (r) => {
      const t = String(r.description ?? "").trim();
      if (!t) return "—";
      return t.length > 100 ? `${t.slice(0, 100)}…` : t;
    },
  },
  {
    key: "status",
    label: "Status",
    width: "100px",
    render: (r) => (
      <span className={`adm-status-pill${rowIsActive(r) ? " adm-status-pill--active" : " adm-status-pill--inactive"}`}>
        {rowIsActive(r) ? "Active" : "Inactive"}
      </span>
    ),
  },
];

export default function AdminGalleryCategories() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [drawerMode, setDrawerMode] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", slug: "", description: "" });
  const rowActions = useAdminRowActionsMenu();

  const reload = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const isActiveParam = filter === "active" ? true : filter === "inactive" ? false : undefined;
      const [allRes, filteredRes] = await Promise.all([
        fetchGalleryCategories(),
        isActiveParam === undefined ? fetchGalleryCategories() : fetchGalleryCategories(isActiveParam),
      ]);
      setAllRecords(allRes.data || []);
      setRecords(isActiveParam === undefined ? allRes.data || [] : filteredRes.data || []);
    } catch (err) {
      setListError(err.message || "Could not load categories.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [filter, navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const cancelAdd = useCallback(() => {
    setAddOpen(false);
    setCategoryName("");
    setFormError("");
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerMode(null);
    setEditingRow(null);
    setEditForm({ name: "", slug: "", description: "" });
    setFormError("");
    rowActions.setActionMenu(null);
  }, [rowActions]);

  const openView = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name ?? "", slug: row.slug ?? "", description: row.description ?? "" });
    setDrawerMode("view");
  };

  const openUpdate = (row) => {
    setEditingRow(row);
    setEditForm({ name: row.name ?? "", slug: row.slug ?? "", description: row.description ?? "" });
    setDrawerMode("edit");
    setFormError("");
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editingRow) return;
    const name = String(editForm.name).trim();
    if (!name) return;
    setSaving(true);
    setFormError("");
    try {
      await updateGalleryCategory(editingRow.id, {
        name,
        slug: String(editForm.slug ?? "").trim(),
        description: String(editForm.description ?? "").trim(),
      });
      closeDrawer();
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not save category.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!addOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") cancelAdd();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [addOpen, cancelAdd]);

  const handleAddCategory = async (e) => {
    e.preventDefault();
    const name = String(categoryName).trim();
    if (!name) return;
    setSaving(true);
    setFormError("");
    try {
      await createGalleryCategory({ name, slug: "", description: "" });
      setCategoryName("");
      setAddOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message || "Could not create category.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const setCategoryActive = async (row, active) => {
    try {
      await updateGalleryCategory(row.id, { active });
      await reload();
    } catch (err) {
      setListError(err.message || "Could not update status.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    }
  };

  const counts = useMemo(() => {
    const active = allRecords.filter((r) => rowIsActive(r)).length;
    return { all: allRecords.length, active, inactive: allRecords.length - active };
  }, [allRecords]);

  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const ao = Number(a.sortOrder) || 0;
      const bo = Number(b.sortOrder) || 0;
      if (ao !== bo) return ao - bo;
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [records]);

  const tableColumns = useMemo(() => {
    const mapped = CATEGORY_COLUMNS.map((c) => ({
      id: c.key,
      header: c.label,
      width: c.width,
      tdClassName: c.tdClassName,
      render: (row) => (c.render ? c.render(row) : String(row[c.key] ?? "—")),
    }));
    return [
      ...mapped,
      buildActionsColumn({
        rowActions,
        getRowLabel: (row) => row.name || "category",
      }),
    ];
  }, [rowActions]);

  return (
    <div className="adm-page adm-page--wide">
      <div className="adm-embedded-simple-crud">
        <header className="adm-page-head adm-page-head--embedded adm-page-head--row">
          <h2 className="adm-page-title adm-page-title--embedded">Image Category Master</h2>
          <button type="button" className="adm-btn adm-btn--primary" onClick={() => setAddOpen(true)} disabled={addOpen}>
            Add category
          </button>
        </header>

        {listError ? (
          <p className="adm-field-error" role="alert">
            {listError}
          </p>
        ) : null}

        <div className="adm-banner-toolbar adm-banner-toolbar--filters-only">
          <div className="adm-banner-toolbar-left" role="tablist" aria-label="Filter image categories by status">
            <button
              type="button"
              role="tab"
              aria-selected={filter === "all"}
              className={`adm-filter-tab${filter === "all" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("all")}
            >
              All ({counts.all})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "active"}
              className={`adm-filter-tab${filter === "active" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("active")}
            >
              Active ({counts.active})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={filter === "inactive"}
              className={`adm-filter-tab${filter === "inactive" ? " adm-filter-tab--active" : ""}`}
              onClick={() => setFilter("inactive")}
            >
              Inactive ({counts.inactive})
            </button>
          </div>
        </div>

        {addOpen &&
          createPortal(
            <div className="adm-drawer-root">
              <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={cancelAdd} />
              <aside className="adm-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="adm-gallery-cat-drawer-title">
                <div className="adm-drawer-panel-inner">
                  <header className="adm-drawer-header">
                    <h2 id="adm-gallery-cat-drawer-title" className="adm-drawer-title">
                      Add category
                    </h2>
                    <button type="button" className="adm-drawer-close" onClick={cancelAdd} aria-label="Close">
                      <span aria-hidden>×</span>
                    </button>
                  </header>
                  <form className="adm-drawer-form" onSubmit={handleAddCategory}>
                    <div className="adm-drawer-fields">
                      <label className="adm-field">
                        <span className="adm-field-label">
                          Category Name<span className="adm-req"> *</span>
                        </span>
                        <input
                          className="adm-input"
                          value={categoryName}
                          onChange={(e) => {
                            setCategoryName(e.target.value);
                            setFormError("");
                          }}
                          placeholder="e.g. Sports, Annual Day"
                          autoFocus
                          autoComplete="off"
                        />
                        <p className="adm-upload-hint">Category names must be unique (not case-sensitive).</p>
                      </label>
                      {formError ? (
                        <p className="adm-field-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                    </div>
                    <footer className="adm-drawer-footer">
                      <button type="button" className="adm-btn adm-btn--ghost" onClick={cancelAdd}>
                        Cancel
                      </button>
                      <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                      </button>
                    </footer>
                  </form>
                </div>
              </aside>
            </div>,
            document.body
          )}

        {(drawerMode === "view" || drawerMode === "edit") &&
          createPortal(
            <div className="adm-drawer-root">
              <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={closeDrawer} />
              <aside className="adm-drawer-panel" role="dialog" aria-modal="true">
                <div className="adm-drawer-panel-inner">
                  <header className="adm-drawer-header">
                    <h2 className="adm-drawer-title">{drawerMode === "view" ? "View category" : "Update category"}</h2>
                    <button type="button" className="adm-drawer-close" onClick={closeDrawer} aria-label="Close">
                      <span aria-hidden>×</span>
                    </button>
                  </header>
                  <form className="adm-drawer-form" onSubmit={saveEdit}>
                    <div className="adm-drawer-fields">
                      <label className="adm-field">
                        <span className="adm-field-label">Name</span>
                        <input
                          className="adm-input"
                          value={editForm.name}
                          onChange={(e) => {
                            setEditForm((f) => ({ ...f, name: e.target.value }));
                            setFormError("");
                          }}
                          readOnly={drawerMode === "view"}
                          disabled={drawerMode === "view"}
                        />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label">Slug</span>
                        <input
                          className="adm-input"
                          value={editForm.slug}
                          onChange={(e) => setEditForm((f) => ({ ...f, slug: e.target.value }))}
                          readOnly={drawerMode === "view"}
                          disabled={drawerMode === "view"}
                        />
                      </label>
                      <label className="adm-field">
                        <span className="adm-field-label">Description</span>
                        <textarea
                          className="adm-input adm-input--textarea"
                          rows={4}
                          value={editForm.description}
                          onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                          readOnly={drawerMode === "view"}
                          disabled={drawerMode === "view"}
                        />
                      </label>
                      {formError ? (
                        <p className="adm-field-error" role="alert">
                          {formError}
                        </p>
                      ) : null}
                    </div>
                    <footer className="adm-drawer-footer">
                      <button type="button" className="adm-btn adm-btn--ghost" onClick={closeDrawer}>
                        {drawerMode === "view" ? "Close" : "Cancel"}
                      </button>
                      {drawerMode === "edit" ? (
                        <button type="submit" className="adm-btn adm-btn--primary" disabled={saving}>
                          {saving ? "Saving…" : "Save changes"}
                        </button>
                      ) : null}
                    </footer>
                  </form>
                </div>
              </aside>
            </div>,
            document.body
          )}

        <AdminRowActionsMenu
          actionMenu={rowActions.actionMenu}
          onClose={rowActions.close}
          ariaLabel="Image category actions"
          isActive={rowActions.actionMenu ? rowIsActive(rowActions.actionMenu.row) : false}
          onView={(row) => openView(row)}
          onUpdate={(row) => openUpdate(row)}
          onToggleActive={(row) => setCategoryActive(row, !rowIsActive(row))}
        />

        <section className="adm-card adm-card--table">
          {loading ? (
            <p className="adm-empty">Loading…</p>
          ) : (
            <AdminDataTable
              layout="fixed"
              columns={tableColumns}
              rows={sortedRecords}
              getRowKey={(r) => r.id}
              emptyState={
                allRecords.length === 0 ? (
                  <span className="adm-empty adm-empty--in-table">No image categories yet.</span>
                ) : (
                  <span className="adm-empty adm-empty--in-table">No categories match this filter.</span>
                )
              }
            />
          )}
        </section>
      </div>
    </div>
  );
}
